import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DynamicListComponent } from "../dynamic-list/dynamic-list.component";
import { CreditCardReportTileComponent } from "../credit-card-report-tile/credit-card-report-tile.component";
import { Pagination } from "../../services/pagination.service";
import { CreditCardReportItem, CreditCardReportType } from "../../models/reports.model";
import { BehaviorSubject } from "rxjs";
import { shareReplay, take, tap } from "rxjs/operators";
import { ReportsService } from "../../services/reports.service";
import { BusinessService } from "../../services/business.service";
import { SupabaseService } from "../../services/supabase.service";
import { ViewAsService } from "../../services/view-as.service";
import { dateString } from "../../../../../common/src/lib/services";
import { BusinessProfile } from "../../../../../common/src/lib/models/business-profile.model";
import { limitStep } from "../../services/reports-service-extras";
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { ReportsCreditCardTab } from '../../models/navigation.model';

function creditCardItemsToPaginationList(items: CreditCardReportItem[], kind: CreditCardReportType) {
  return items.map(item => ({
    componentClass: CreditCardReportTileComponent,
    header: dateString(item.createdAt),
    args: { item, kind }
  }));
}

@Component({
  selector: 'app-credit-card-report-items',
  templateUrl: './credit-card-report-items.component.html',
  styleUrls: ['./credit-card-report-items.component.scss']
})
export class CreditCardReportItemsComponent implements OnInit, OnDestroy {

  @Input() current!: ReportsCreditCardTab;

  @ViewChild('settledList') settledList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('settledDateFilterList') settledDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('pendingList') pendingList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('pendingDateFilterList') pendingDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('preChargebacksList') preChargebacksList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('preChargebacksDateFilterList') preChargebacksDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('chargebacksList') chargebacksList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('chargebacksDateFilterList') chargebacksDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('refundsList') refundsList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('refundsDateFilterList') refundsDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('surchargeList') surchargeList!: DynamicListComponent<CreditCardReportTileComponent>;
  @ViewChild('surchargeDateFilterList') surchargeDateFilterList!: DynamicListComponent<CreditCardReportTileComponent>;

  settledPagination!: Pagination<CreditCardReportItem>;
  settledDateFilterPagination!: Pagination<CreditCardReportItem>;
  pendingPagination!: Pagination<CreditCardReportItem>;
  pendingDateFilterPagination!: Pagination<CreditCardReportItem>;
  preChargebacksPagination!: Pagination<CreditCardReportItem>;
  preChargebacksDateFilterPagination!: Pagination<CreditCardReportItem>;
  chargebacksPagination!: Pagination<CreditCardReportItem>;
  chargebacksDateFilterPagination!: Pagination<CreditCardReportItem>;
  refundsPagination!: Pagination<CreditCardReportItem>;
  refundsDateFilterPagination!: Pagination<CreditCardReportItem>;
  surchargePagination!: Pagination<CreditCardReportItem>;
  surchargeDateFilterPagination!: Pagination<CreditCardReportItem>;

  loading$ = new BehaviorSubject<boolean>(true);

  dateFilter$ = this.reportsService.selectedDateRangeForEvent$.pipe(
    tap(async dates => {
      if (dates) {
        this.loadItems('pending', 0, undefined, dates, true);
        this.loadItems('settled', 0, undefined, dates, true);
        this.loadItems('pre-chargeback', 0, undefined, dates, true);
        this.loadItems('chargeback', 0, undefined, dates, true);
        this.loadItems('refund', 0, undefined, dates, true);
        this.loadItems('surcharge', 0, undefined, dates, true);
      } else {
        this.settledDateFilterList?.clear();
        this.pendingDateFilterList?.clear();
        this.preChargebacksDateFilterList?.clear();
        this.chargebacksDateFilterList?.clear();
        this.refundsDateFilterList?.clear();
        this.surchargeDateFilterList?.clear();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true})
  );

  constructor(
    private reportsService: ReportsService,
    private businessService: BusinessService,
    private supabaseService: SupabaseService,
    private viewAsService: ViewAsService,
  ) { }

  async ngOnInit() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    // TODO: add tables
    this.settledPagination = this.createPagination(business.businessId, 'settled_report', 'c_settled')

    this.settledDateFilterPagination = this.createPagination(business.businessId, 'settled_report', 'c_settled_d')

    this.pendingPagination = this.createPagination(business.businessId, 'pending_report', 'c_pen')

    this.pendingDateFilterPagination = this.createPagination(business.businessId, 'pending_report', 'c_pen_d')

    // this.preChargebacksPagination = this.createPagination(business.businessId, 'cc_pre_chargeback_report', 'c_pre')
    //
    // this.preChargebacksDateFilterPagination = this.createPagination(business.businessId, 'cc_pre_chargeback_report', 'c_pre_d')
    //
    // this.chargebacksPagination = this.createPagination(business.businessId, 'cc_chargeback_report', 'c_ch')
    //
    // this.chargebacksDateFilterPagination = this.createPagination(business.businessId, 'cc_chargeback_report', 'c_ch_d')

    this.refundsPagination = this.createPagination(business.businessId, 'refunds_report', 'c_ref')

    this.refundsDateFilterPagination = this.createPagination(business.businessId, 'refunds_report', 'c_ref_d')

    this.surchargePagination = this.createPagination(business.businessId, 'surcharge_report', 'c_sur')

    this.surchargeDateFilterPagination = this.createPagination(business.businessId, 'surcharge_report', 'c_sur_d')


    await this.loadItems('settled', 0, business, undefined, false);
    await this.loadItems('pending', 0, business, undefined, false);
    // await this.loadItems('pre-chargeback', 0, business, undefined, false);
    // await this.loadItems('chargeback', 0, business, undefined, false);
    await this.loadItems('refund', 0, business, undefined, false);
    await this.loadItems('surcharge', 0, business, undefined, false);
    this.loading$.next(false);
  }

  ngOnDestroy() {
    this.settledPagination?.destroy();
    this.settledDateFilterPagination?.destroy();
    this.pendingPagination?.destroy();
    this.pendingDateFilterPagination?.destroy();
    this.preChargebacksPagination?.destroy();
    this.preChargebacksDateFilterPagination?.destroy();
    this.chargebacksPagination?.destroy();
    this.chargebacksDateFilterPagination?.destroy();
    this.refundsPagination?.destroy();
    this.refundsDateFilterPagination?.destroy();
    this.surchargePagination?.destroy();
    this.surchargeDateFilterPagination?.destroy();
  }

  async loadItems(
    type: CreditCardReportType,
    inOffset = 0,
    business?: BusinessProfile,
    dates?: [Date, Date] | null,
    dateList?: boolean,
    updateList?: boolean
  ) {
    if (!business)
      business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    if (dateList && !dates)
      dates = await this.dateFilter$.pipe(take(1)).toPromise();
    if (!dates)
      dates = [business.createdAt, new Date()];

    const selectedUsers = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();

    let pagination: Pagination<CreditCardReportItem>;
    let list: DynamicListComponent<CreditCardReportTileComponent>;
    const paginationMap = {
      'pending': dateList ? this.pendingDateFilterPagination : this.pendingPagination,
      'settled': dateList ? this.settledDateFilterPagination : this.settledPagination,
      'pre-chargeback': dateList ? this.preChargebacksDateFilterPagination : this.preChargebacksPagination,
      'chargeback': dateList ? this.chargebacksDateFilterPagination : this.chargebacksPagination,
      'refund': dateList ? this.refundsDateFilterPagination : this.refundsPagination,
      'surcharge': dateList ? this.surchargeDateFilterPagination : this.surchargePagination,
    };

    const listMap = {
      'pending': dateList ? this.pendingDateFilterList : this.pendingList,
      'settled': dateList ? this.settledDateFilterList : this.settledList,
      'pre-chargeback': dateList ? this.preChargebacksDateFilterList : this.preChargebacksList,
      'chargeback': dateList ? this.chargebacksDateFilterList : this.chargebacksList,
      'refund': dateList ? this.refundsDateFilterList : this.refundsList,
      'surcharge': dateList ? this.surchargeDateFilterList : this.surchargeList,
    };

    pagination = paginationMap[type];
    list = listMap[type];

    const inLimit = updateList && limitStep < list.getBottomCount ? list.getBottomCount : limitStep;

    const items = await pagination.get({
      startTime: dates[0],
      endTime: dates[1],
      rowLimit: inLimit,
      offsetCount: inOffset,
      selectedUsers: selectedUsers ?? []
    });

    if (items.length < inLimit && list.canLoadMoreBottom)
      list.canLoadMoreBottom = false;

    if (!inOffset)
      list.clear();
    list?.addAtBottom(creditCardItemsToPaginationList(items, type));
  }

  async handleChange(type: string, dateList = false) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const dates = await this.dateFilter$.pipe(take(1)).toPromise();

    this.loadItems(
      type as CreditCardReportType,
      0,
      business!,
      dateList ? dates : null,
      dateList,
      true
    );
  }

  createPagination(businessId: string, fn: string, cname: string) {
    return new Pagination<CreditCardReportItem>(
      this.supabaseService,
      fn,
      businessId,
      cname
    );
  }
}
